import React, { useMemo } from 'react';
import VirtualCardBgIcon from 'assets/icons/VirtualCardBgIcon.svg';
import PhysicalCardBgIcon from 'assets/icons/PhysicalCardBgIcon.svg';
import { ETREASURY_CARD_STATUS, ETREASURY_CARD_TYPES } from '../constants';

interface CardBackgroundProps {
  cardType: string;
  cardStatus: string;
  isPhysicalCard: boolean;
  getElementHeight: (height: number) => number;
  children: React.ReactNode;
  className?: string;
}

const CardBackground: React.FC<CardBackgroundProps> = ({
  cardType,
  cardStatus,
  isPhysicalCard,
  getElementHeight,
  children,
  className = ''
}) => {
  // Memoize background image selection
  const backgroundImage = useMemo(() => {
    return cardType.toLowerCase() === ETREASURY_CARD_TYPES.VIRTUAL
      ? `url(${VirtualCardBgIcon})`
      : `url(${PhysicalCardBgIcon})`;
  }, [cardType]);

  // Memoize status overlay visibility and styles
  const statusOverlay = useMemo(() => {
    const shouldShowStatusBg = isPhysicalCard
      ? cardStatus !== ETREASURY_CARD_STATUS.ACTIVE
      : cardStatus === ETREASURY_CARD_STATUS.INACTIVE;

    if (!shouldShowStatusBg) return null;

    const overlayStyle = {
      background: isPhysicalCard
        ? 'linear-gradient(to right, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1))'
        : '#21242926',
      boxShadow: '0px 18.7px 29.93px -7.48px #162A400F'
    };

    return (
      <div
        className="pointer-events-none absolute inset-0 h-full w-full bg-cover bg-center bg-no-repeat"
        style={overlayStyle}
        aria-hidden="true"
      />
    );
  }, [isPhysicalCard, cardStatus]);

  return (
    <div
      className={`relative w-full overflow-hidden rounded-xl ${className}`}
      role="img"
      style={{ height: getElementHeight(220) }}
      aria-label="Card display">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 h-full w-full bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage }}
        aria-hidden="true"
      />

      {/* Status Overlay */}
      {statusOverlay}

      {/* Card Content Container */}
      <div className="relative z-10 flex h-full flex-col justify-between rounded-[14px] p-5">
        <div className="flex h-full w-full flex-col justify-between">{children}</div>
      </div>
    </div>
  );
};

export default React.memo(CardBackground);
